import axios from 'axios';

// Safely access environment variables
const getEnvVar = (key, fallback) => {
  try {
    return (typeof process !== 'undefined' && process.env && process.env[key]) || fallback;
  } catch (e) {
    return fallback;
  }
};

const PREFIX_URL = getEnvVar('REACT_APP_TAG_SERVICE_URL', 'http://localhost:8082');
const BASE_URL = `${PREFIX_URL}/api/v1/tag-management`;
const API_URL = `${PREFIX_URL}/api/v1`;

const handleApiError = (error) => {
  console.error('API Error:', error);
  throw error;
};

// Cache Layer
// ======================

const inFlightCache = new Map();
const resultCache = new Map();

const normalizeArgs = (args) => {
  return JSON.stringify(
    args.map((a) => {
      if (typeof a === 'object' && a !== null) {
        return Object.fromEntries(Object.entries(a).sort());
      }
      return a;
    })
  );
};

const withTimeout = (promise, ms = 30000) =>
  Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Request timed out')), ms)
    ),
  ]);

const withCache = (fn, options = {}) => {
  const ttl = options.ttl || 1000;
  const id = options.id || fn.name;

  const wrapper = async (...args) => {
    const cacheKey = `${id}:${normalizeArgs(args)}`;
    const now = Date.now();

    const cached = resultCache.get(cacheKey);
    if (cached && now - cached.timestamp < ttl) {
      return cached.data;
    }

    if (inFlightCache.has(cacheKey)) {
      return inFlightCache.get(cacheKey);
    }

    const promise = (async () => {
      try {
        const data = await withTimeout(fn(...args), 10000); // 10s timeout
        resultCache.set(cacheKey, { data, timestamp: Date.now() });
        return data;
      } catch (err) {
        console.warn(`[CACHE] Error in "${id}" for ${cacheKey}:`, err);
        throw err;
      } finally {
        inFlightCache.delete(cacheKey);
      }
    })();

    inFlightCache.set(cacheKey, promise);
    return promise;
  };

  wrapper.clearCache = () => {
    [...resultCache.keys()].forEach((key) => {
      if (key.startsWith(id + ':')) {
        resultCache.delete(key);
        inFlightCache.delete(key);
      }
    });
  };

  return wrapper;
};

// ======================
// Raw API Methods
// ======================

const getTagsRaw = async (page = 1, limit = 10, search = '', entityTypeId = null) => {
  const params = { page, limit, search };
  if (entityTypeId) params.entity_type_id = entityTypeId;

  const response = await axios.get(`${BASE_URL}/tags`, { params });
  if (!response.data.tags) response.data.tags = [];

  return response.data;
};

const getEntityTagsRaw = async (entityTypeId, entityId) => {
  return axios
    .get(`${BASE_URL}/entity-tags/${entityTypeId}/${entityId}`)
    .then((response) => response.data)
    .catch(handleApiError);
};

const getBatchEntityTagsRaw = async (entityTypeId, entityIds) => {
  try {
    const payload = { entity_ids: entityIds.map(id => id.toString()) };
    const response = await axios.post(`${BASE_URL}/entity-types/${entityTypeId}/batch-tags`, payload);
    return response.data;
  } catch (error) {
    console.error('[tagApi] Error in batch tags API:', error?.response?.data || error?.message || error);
    handleApiError(error);
    // Return a valid but empty response structure to avoid breaking the UI
    return { tags_by_entity: {} };
  }
};

const getEntityTypesRaw = async () => {
  const response = await axios.get(`${BASE_URL}/entity-types`);
  return response.data;
};

// ======================
// Cached Methods
// ======================

const getTags = withCache(getTagsRaw, { ttl: 30000, id: 'getTags' });
const getEntityTags = withCache(getEntityTagsRaw, { ttl: 30000, id: 'getEntityTags' });
const getBatchEntityTags = withCache(getBatchEntityTagsRaw, { ttl: 30000, id: 'getBatchEntityTags' });
const getEntityTypes = withCache(getEntityTypesRaw, { ttl: 3000, id: 'getEntityTypes' });

// ======================
// tagApi
// ======================

const tagApi = {
  getTags,
  getEntityTags,
  getBatchEntityTags,
  getEntityTypes,

  createTag: async (tagData) => {
    try {
      const response = await axios.post(`${BASE_URL}/tags`, tagData);
      if (getTags.clearCache) getTags.clearCache();
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  updateTag: async (tagId, tagData) => {
    try {
      console.log(`[DEBUG] Updating tag ${tagId} with data:`, tagData);
      const response = await axios.put(`${BASE_URL}/tags/${tagId}`, tagData);
      console.log(`[DEBUG] Tag update response:`, response.data);
      if (getTags.clearCache) getTags.clearCache();
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  deleteTag: async (tagId) => {
    try {
      const response = await axios.delete(`${BASE_URL}/tags/${tagId}`);
      if (getTags.clearCache) getTags.clearCache();
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  getEntitiesByTag: async (entityType, tagId) => {
    try {
      const apiUrl = `${PREFIX_URL}/api/v1/tag-management/entity-tags/${entityType}/${tagId}/entities`;
      const startTime = Date.now();
      const response = await axios.get(apiUrl);
      const endTime = Date.now();

      console.log(`DEBUG: Tag API response time: ${endTime - startTime}ms`);
      console.log(`DEBUG: Tag API response data:`, JSON.stringify(response.data));

      return response.data;
    } catch (error) {
      console.error('DEBUG: Error fetching entities by tag:', error);
      return { entity_ids: [] };
    }
  },

  addTagToEntity: async (tagAssignment) => {
    try {
      const response = await axios.post(`${BASE_URL}/entity-tags`, {
        entity_type_id: tagAssignment.entity_type_id,
        entity_id: tagAssignment.entity_id,
        tag_id: tagAssignment.tag_id,
      });
      if (getEntityTags.clearCache) getEntityTags.clearCache();
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  removeTagFromEntity: async (entityTypeId, entityId, tagId) => {
    try {
      const response = await axios.delete(`${BASE_URL}/entity-tags/${entityTypeId}/${entityId}/${tagId}`);
      if (getEntityTags.clearCache) getEntityTags.clearCache();
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  createEntityType: async (entityTypeData) => {
    try {
      const response = await axios.post(`${BASE_URL}/entity-types`, {
        name: entityTypeData.name,
        description: entityTypeData.description,
      });
      if (getEntityTypes.clearCache) getEntityTypes.clearCache();
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  updateEntityType: async (entityTypeId, entityTypeData) => {
    try {
      const response = await axios.put(`${BASE_URL}/entity-types/${entityTypeId}`, entityTypeData);
      if (getEntityTypes.clearCache) getEntityTypes.clearCache();
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  getAllTagAssignments: async (page = 1, limit = 50, entityTypeId = null, entityId = null) => {
    try {
      const params = { page, limit };
      if (entityTypeId) params.entity_type_id = entityTypeId;
      if (entityId) params.entity_id = entityId;

      const response = await axios.get(`${BASE_URL}/entity-tags/all`, { params });
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  getAvailablePermissions: async () => {
    try {
      const response = await axios.get(`${BASE_URL}/permissions`);
      return response.data;
    } catch (error) {
      console.error('Error fetching permissions:', error);
      return { permissions: [], categories: {} };
    }
  },

  updateEntityTags: async (entityTypeId, entityId, newTags) => {
    try {
      console.log(`[DEBUG] Updating tags for entity ${entityId} with ${newTags.length} tags`);
      
      // First, get current tags for this entity
      const currentTagsResponse = await axios.get(`${BASE_URL}/entity-tags/${entityTypeId}/${entityId}`);
      const currentTags = Array.isArray(currentTagsResponse.data) 
        ? currentTagsResponse.data 
        : (currentTagsResponse.data.tags || []);
      
      console.log(`[DEBUG] Current tags:`, currentTags);
      
      // Determine which tags to add and which to remove
      const currentTagIds = currentTags.map(tag => tag.id);
      const newTagIds = newTags.map(tag => tag.id);
      
      const tagsToAdd = newTagIds.filter(id => !currentTagIds.includes(id));
      const tagsToRemove = currentTagIds.filter(id => !newTagIds.includes(id));
      
      console.log(`[DEBUG] Tags to add:`, tagsToAdd);
      console.log(`[DEBUG] Tags to remove:`, tagsToRemove);
      
      // Process each operation individually with proper error handling
      // Add new tags one by one with error handling for duplicates
      for (const tagId of tagsToAdd) {
        try {
          await axios.post(`${BASE_URL}/entity-tags`, {
            entity_type_id: entityTypeId,
            entity_id: entityId,
            tag_id: tagId
          });
        } catch (err) {
          // Ignore "already assigned" errors but log them
          if (err.response?.data?.detail === "Tag is already assigned to this entity") {
            console.log(`[DEBUG] Tag ${tagId} already assigned, skipping`);
          } else {
            // For other errors, log and continue
            console.error(`[DEBUG] Error adding tag ${tagId}:`, err);
          }
        }
      }
      
      // Remove tags one by one
      for (const tagId of tagsToRemove) {
        try {
          await axios.delete(`${BASE_URL}/entity-tags/${entityTypeId}/${entityId}/${tagId}`);
        } catch (err) {
          // Log error but continue with other operations
          console.error(`[DEBUG] Error removing tag ${tagId}:`, err);
        }
      }
      
      // Clear the cache for this entity's tags
      if (getEntityTags.clearCache) getEntityTags.clearCache();
      if (getBatchEntityTags.clearCache) getBatchEntityTags.clearCache();
      
      console.log(`[DEBUG] Tags updated successfully for entity ${entityId}`);
      
      // Return the new tags
      return { success: true, tags: newTags };
    } catch (error) {
      console.error('[DEBUG] Error updating entity tags:', error);
      handleApiError(error);
    }
  }
};

export default tagApi;
